import React, { useCallback, useEffect, useMemo } from "react";
import { useField, useForm } from "react-final-form";
import { Layers as LayersIcon } from "@mui/icons-material";
import { Box, Skeleton, TextField, Typography } from "@mui/material";
import classNames from "classnames";

import { Table, TableBody, TableHead } from "ui";

import { BodyCell, CompactFormControl, HeaderCell, ModifiedTableRow } from "fond/export/ExportTable/ExportTable.styles";
import { ResourceAutocomplete } from "fond/import/modal/ProjectVersionSelection/ProjectVersionSelection";
import { InferredIcon } from "fond/map/Legend";
import { Configuration } from "fond/types";
import { LayerConfig } from "fond/types/ProjectLayerConfig";
import { selectLayersFromConfig } from "fond/utils/configurations";
import { fuzzyMatchLayers } from "fond/utils/layers";

import { convertLayerMappings, ignoreLayerOption, resolveLayerConfigurationInputLayers, resolveLayerConfigurationNameFromFilename } from "./helper";
import { FormData } from "./SetupForm";

import { IconBox, LayerHeaderBox, LayerTableContainer } from "./LayerSelectionStep.styles";

interface LayerSelectionStepProps {
  config: Configuration;
  isReadOnly?: boolean;
}

const LayerSelectionStep: React.FC<LayerSelectionStepProps> = ({ config, isReadOnly }) => {
  const { change } = useForm();
  const {
    input: { value: layersConfiguration },
  } = useField<FormData["Inputs"]["ToolLayersConfiguration"]["LayersConfiguration"]["Data"]>(
    "Inputs.ToolLayersConfiguration.LayersConfiguration.Data"
  );

  const {
    meta: { error: stepError, touched, dirty },
    input: { value: layerMappings },
  } = useField<FormData["Inputs"]["ToolLayersConfiguration"]["Mappings"]>("Inputs.ToolLayersConfiguration.Mappings", {
    validate: (value) => {
      if (!value || Object.keys(value).length === 0) {
        return "At least one layer must be mapped.";
      }
      return undefined;
    },
  });

  const layerConfigs = useMemo(() => [ignoreLayerOption, ...selectLayersFromConfig(config, ["LAYER"])] as LayerConfig[], [config]);

  // Get the C2S layers from the configuration
  const c2sLayers = useMemo(() => {
    const layerNames = layersConfiguration ? Array.from(resolveLayerConfigurationInputLayers(layersConfiguration)) : [];
    return layerNames.map((name) => ({ name, displayName: resolveLayerConfigurationNameFromFilename(layersConfiguration!, name) }));
  }, [layersConfiguration]);

  useEffect(() => {
    // Auto-map layers using fuzzy matching only if there are no existing mappings
    if (dirty) return;

    const suggestions = fuzzyMatchLayers(
      c2sLayers.map((layer) => layer.name),
      layerConfigs
    );

    // Initialize mappings for all C2S layers
    change(
      "Inputs.ToolLayersConfiguration.Mappings",
      convertLayerMappings(Object.fromEntries(c2sLayers.map(({ name: layerName }) => [layerName, suggestions?.[layerName] ?? null])))
    );
  }, [c2sLayers, change, dirty, layerConfigs, layersConfiguration]);

  const handleLayerMappingChange = useCallback(
    (c2sLayerName: string, selectedLayer: LayerConfig | null) => {
      const newValue: FormData["Inputs"]["ToolLayersConfiguration"]["Mappings"] = layerMappings?.filter((m) => m.Name !== c2sLayerName);

      if (selectedLayer?.Key) {
        newValue.push({
          Name: c2sLayerName,
          Layer: {
            LayerKey: selectedLayer.Key,
          },
        });
      }

      change("Inputs.ToolLayersConfiguration.Mappings", newValue);
    },
    [change, layerMappings]
  );

  if (!layersConfiguration) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Layer Selection
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please upload a layers configuration first.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Layer Association
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Associate your layers configuration with layers in your project.
      </Typography>

      <Box>
        <LayerHeaderBox>
          <Box display="flex" margin="auto 0">
            <LayersIcon
              style={{
                marginLeft: 4,
                marginRight: 8,
              }}
            />
            <Typography variant="h6" component="p">
              {c2sLayers.length} Layers
            </Typography>
          </Box>
        </LayerHeaderBox>

        <LayerTableContainer>
          <Table stickyHeader size="small">
            <TableHead>
              <ModifiedTableRow>
                <HeaderCell>
                  Configuration Item{" "}
                  {stepError && touched && (
                    <Typography color="error" component="span">
                      * {stepError}
                    </Typography>
                  )}
                </HeaderCell>
                <HeaderCell>Select layer</HeaderCell>
              </ModifiedTableRow>
            </TableHead>
            <TableBody className={classNames("customScrollbars")} sx={{ display: "block", overflow: "auto" }}>
              {c2sLayers.map(({ name: c2sLayerName, displayName }) => {
                const mapping = layerMappings.find(({ Name }) => Name === c2sLayerName);
                const value = layerConfigs.find((lc) => lc.Key === mapping?.Layer.LayerKey) || ignoreLayerOption;
                return (
                  <ModifiedTableRow key={c2sLayerName} sx={{ height: "42px" }}>
                    <BodyCell>
                      <Typography variant="body2">{displayName}</Typography>
                    </BodyCell>
                    <BodyCell>
                      <CompactFormControl width={194} size="small">
                        {!config ? (
                          <Skeleton height={42} />
                        ) : (
                          <ResourceAutocomplete
                            sx={{ backgroundColor: "white" }}
                            value={value}
                            onChange={(_, newValue) => handleLayerMappingChange(c2sLayerName, newValue)}
                            options={layerConfigs}
                            getOptionLabel={(option) => option?.Label || ""}
                            disabled={isReadOnly}
                            renderOption={(option: LayerConfig) => {
                              return (
                                <Box data-testid="option-configuration" display="flex">
                                  {option.ID !== ignoreLayerOption.ID ? (
                                    <IconBox>
                                      <InferredIcon entity={option} config={config} />
                                    </IconBox>
                                  ) : (
                                    <IconBox>
                                      <LayersIcon />
                                    </IconBox>
                                  )}
                                  <Typography data-testid="option-label">{option.Label}</Typography>
                                </Box>
                              );
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                InputProps={{
                                  ...params.InputProps,
                                  startAdornment:
                                    value.ID && config ? (
                                      <IconBox>
                                        <InferredIcon entity={value} config={config} />
                                      </IconBox>
                                    ) : (
                                      <IconBox>
                                        <LayersIcon />
                                      </IconBox>
                                    ),
                                }}
                                placeholder="Select a layer..."
                                variant="outlined"
                              />
                            )}
                          />
                        )}
                      </CompactFormControl>
                    </BodyCell>
                  </ModifiedTableRow>
                );
              })}
            </TableBody>
          </Table>
        </LayerTableContainer>
      </Box>
    </Box>
  );
};

export default LayerSelectionStep;
